import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "@marekl/adminer/datetime";
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from "../api";
import type { MembershipKind } from "./memberships";
import type { EntryMusic, MusicSource } from "./music";
import type { ID } from "../id";

/**
 * Competition entries (Modul 5 — přihlášky).
 *
 * An entry references one enabled combination (`discipline_category_id`
 * from the rule-set document) and its competitors by their roster rows.
 * The server is the authority on every check; a 422 arrives as
 * {message, errors:[{field, message, team_membership_id?}]}.
 */

export type EntryStatus = "submitted" | "withdrawn";

/**
 * Why a přihláška's controls are narrowed, ordered by how little the
 * actor can do about it.
 *
 * Machine keys from internal/service/entry/window.go; the Czech sentence
 * lives in frontend/src/lib/entryWindow.ts and nowhere else. A key that
 * arrives without a sentence there renders as NOTHING — no type error,
 * no failing test — so e2e/specs/prihlaska/contract.spec.ts compares the
 * two sides off disk.
 */
export type EntryLockReason =
  | "competition_frozen"
  | "season_closed"
  | "competition_closed"
  | "start_list_published"
  | "withdrawn"
  | "entry_deadline"
  | "category_deadline"
  | "roster_deadline";

/**
 * What the CURRENT user may do to THIS entry right now, decided by the
 * server.
 *
 * Never re-derive any of it from the deadlines. Four rungs crossed with
 * the manager path, the odhláška, the published startovní listina and
 * the active season are one rule, and it is not this side's.
 */
export interface EntryPermissions {
  can_change_category: boolean;
  can_change_competitors: boolean;
  can_withdraw: boolean;
  can_reinstate: boolean;
  can_delete: boolean;
  /** Absent when nothing narrows this actor here. */
  lock_reason?: EntryLockReason;
  /** An odhláška filed now cannot be replaced by a new přihláška and
   * only the pořadatel can undo it. The club is warned before it
   * presses, which is why this travels rather than being worked out
   * from the deadlines here. */
  withdraw_is_final: boolean;
}

/** The reduced Person the entry endpoints serve — a startlist needs a
 * name and a birth date; the full Person carries PII only global
 * admins may read. */
export interface EntryCompetitorPerson {
  id: ID;
  first_name: string;
  last_name: string;
  birth_date: string;
}

export interface EntryCompetitor {
  team_membership_id: ID;
  kind: MembershipKind;
  person: EntryCompetitorPerson;
}

export interface Entry {
  id: ID;
  /** ČP — číslo přihlášky: sequential per series (or per standalone
   * competition), kept for the entry's whole life incl. odhláška and a
   * future postup to the next round. */
  entry_number: number;
  competition_id: ID;
  team_id: ID;
  team_name?: string;
  /** The state a club outside ČNSMS represents; empty for our own clubs. */
  team_country?: string;
  discipline_category_id: ID;
  status: EntryStatus;
  withdrawn_at?: Dayjs;
  withdrawn_note?: string;
  competitors: EntryCompetitor[];
  /** Who provides the recording for this combination (Modul 6). */
  music_source: MusicSource;
  /** The uploaded recording, or null. Explicitly nullable rather than
   * optional: the server always sends the key, so `undefined` here would
   * mean a bug rather than "nothing uploaded". */
  music: EntryMusic | null;
  /** Required, not optional: the server always sends the block, so
   * `undefined` here would be a bug rather than „nothing allowed" —
   * and reading it as the latter is how a control silently disappears. */
  permissions: EntryPermissions;
  created_at: Dayjs;
  updated_at: Dayjs;
  /** Set only by „Vrátit zpět", and only when putting the entry back
   * took the competition past its capacity. Reinstating is not refused
   * on capacity — a manager undoing their own mistake should not have to
   * talk somebody else out of a slot — but it must not happen silently.
   */
  over_capacity?: boolean;
  submitted_now?: number;
  max_capacity?: number;
}

export interface EntryPayload {
  competition_id: ID;
  discipline_category_id: ID;
  team_membership_ids: ID[];
}

/** One row of the 422 envelope; competitor rows carry the roster id. */
export interface EntryErrorRow {
  field: string;
  message: string;
  team_membership_id?: ID;
}

/** Pulls the per-field rows out of an entry 422, or [] for other errors. */
export function entryErrorRows(err: unknown): EntryErrorRow[] {
  if (!(err instanceof ApiError)) return [];
  const body = err.body as { errors?: EntryErrorRow[] } | null;
  if (!body?.errors) return [];
  return body.errors.filter(
    (e) => typeof e.field === "string" && typeof e.message === "string",
  );
}

export function useTeamEntries(teamId: ID | undefined, competitionId?: ID) {
  return useQuery<Entry[]>({
    queryKey: ["teams", teamId, "entries", competitionId ?? "all"],
    enabled: !!teamId,
    queryFn: async () => {
      const suffix = competitionId ? `?competition_id=${competitionId}` : "";
      const res = await apiGet<{ data: Entry[] | null }>(`/teams/${teamId}/entries${suffix}`);
      return res.data ?? [];
    },
  });
}

export function useCompetitionEntries(competitionId: ID | undefined) {
  return useQuery<Entry[]>({
    queryKey: ["competitions", "entries", competitionId],
    enabled: !!competitionId,
    queryFn: async () => {
      const res = await apiGet<{ data: Entry[] | null }>(`/competitions/${competitionId}/entries`);
      return res.data ?? [];
    },
  });
}

// Every entry write invalidates the team's list, the organizer's
// competition list AND the competition queries themselves — the
// derived entry_phase is capacity-based, so the entry that fills the
// last slot must flip the cached phase too.
function useEntryMutation<TData, TVars>(mutationFn: (vars: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation<TData, ApiError, TVars>({
    mutationFn,
    // The wizard shows the refusal against the field that caused it, and the
    // odhláška / delete dialogs in their error slot, so the global toast
    // would be a second report of a failure already on screen.
    meta: { suppressErrorToast: true },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["teams"] }),
        qc.invalidateQueries({ queryKey: ["competitions"] }),
      ]),
  });
}

export function useCreateEntry(teamId: ID) {
  return useEntryMutation<Entry, EntryPayload>((p) => apiPost(`/teams/${teamId}/entries`, p));
}

export function useUpdateEntry() {
  return useEntryMutation<Entry, { id: ID } & Omit<EntryPayload, "competition_id">>(
    ({ id, ...p }) => apiPatch(`/entries/${id}`, p),
  );
}

export function useDeleteEntry() {
  return useEntryMutation<void, ID>((id) => apiDelete(`/entries/${id}`));
}

export function useWithdrawEntry() {
  return useEntryMutation<Entry, { id: ID; note: string }>(({ id, note }) =>
    apiPost(`/entries/${id}/withdraw`, { note }),
  );
}

/**
 * Undoes an odhláška — a club rings back, the injury was nothing.
 *
 * Withdrawing is a status flip rather than a delete precisely so this is
 * possible: entering again would issue a second ČP for the same entrant
 * and lose the original's history. Refused once the start list is
 * published, like withdrawing itself.
 */
export function useReinstateEntry() {
  return useEntryMutation<Entry, ID>((id) => apiPost(`/entries/${id}/reinstate`, {}));
}
